import { Component } from '@theme/component';

const NATIVE_SHOPIFY_BLOCK_KEY = 'email_signup_last_block';

/**
 * Klaviyo runs only when `data-klaviyo-company-id` is set (non-empty after trim).
 * The theme sets that only if Embed Klaviyo is on and the Public API field is
 * filled. Otherwise both data attributes are empty and the form uses Shopify
 * customer signup only.
 */

class EmailSignupFormComponent extends Component {
  isSubmitting = false;

  /** @type {string | undefined} */
  klaviyoListId;

  /** @type {string | undefined} */
  klaviyoCompanyId;

  /** @type {string | undefined} */
  blockId;

  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  emailInput = null;

  /** @type {HTMLButtonElement | null} */
  submitButton = null;

  connectedCallback() {
    super.connectedCallback();

    this.form = this.querySelector('form');
    if (!this.form) return;

    this.emailInput = this.form.querySelector('input[type="email"]');
    this.submitButton = this.form.querySelector('button[type="submit"]');

    const company = (this.dataset.klaviyoCompanyId ?? '').trim();
    const list = (this.dataset.klaviyoListId ?? '').trim();
    this.klaviyoCompanyId = company || undefined;
    this.klaviyoListId = list || undefined;
    this.blockId = this.dataset.blockId;

    this.setAttribute('data-klaviyo-enabled', this.klaviyoCompanyId ? 'true' : 'false');

    this.form.addEventListener('submit', this.#handleSubmit.bind(this), true);

    requestAnimationFrame(() => {
      if (this.klaviyoCompanyId) {
        this.#hideMessages();
      } else {
        this.#filterNativeShopifyMessages();
      }
    });
  }

  /**
   * @param {Event} event
   */
  async #handleSubmit(event) {
    if (this.isSubmitting) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.klaviyoCompanyId) {
      try {
        if (this.blockId) {
          sessionStorage.setItem(NATIVE_SHOPIFY_BLOCK_KEY, this.blockId);
        }
      } catch (e) {}
      return;
    }

    if (!this.emailInput) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const email = this.emailInput.value?.trim();

    if (!this.emailInput.validity.valid) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    if (!email || !this.#isValidEmail(email)) {
      this.#showValidationError();
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.isSubmitting = true;
    this.#setButtonLoading(true);
    this.#hideMessages();

    try {
      const result = await this.#submitToKlaviyo(email);
      if (result.ok) {
        this.#showSuccess();
        this.form?.reset();
      } else {
        this.#showError(result.message);
        return;
      }
    } catch (error) {
      this.#showError();
      return;
    } finally {
      this.isSubmitting = false;
      this.#setButtonLoading(false);
    }
  }

  /**
   * @param {string} email
   * @returns {Promise<{ ok: boolean; message?: string }>}
   */
  async #submitToKlaviyo(email) {
    if (!this.klaviyoCompanyId || !this.klaviyoListId) {
      return { ok: false };
    }

    /** @type {any} */
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
      },
    };

    if (this.klaviyoListId) {
      payload.data.relationships = {
        list: {
          data: {
            type: 'list',
            id: this.klaviyoListId,
          },
        },
      };
    }

    const apiUrl = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoCompanyId}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          revision: '2026-01-15',
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        return { ok: true };
      }

      const data = await response.json().catch(() => null);
      const message = data?.errors?.[0]?.detail || data?.errors?.[0]?.title || undefined;
      return { ok: false, message };
    } catch (error) {
      return { ok: false };
    }
  }

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  #showSuccess() {
    const successMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const klaviyoErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoErrorMessage) {
      klaviyoErrorMessage.hidden = true;
    }

    if (successMessage) {
      successMessage.hidden = false;
      successMessage.focus();
      this.emailInput?.setAttribute('aria-describedby', successMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  #showValidationError() {
    const klaviyoErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const klaviyoSuccessMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoSuccessMessage) {
      klaviyoSuccessMessage.hidden = true;
    }

    if (klaviyoErrorMessage) {
      const messageText = klaviyoErrorMessage.querySelector('.email-signup__message-text');
      if (messageText) {
        const validationText = messageText.getAttribute('data-validation-text');
        if (validationText) {
          messageText.textContent = validationText;
        }
      }
      klaviyoErrorMessage.hidden = false;
      klaviyoErrorMessage.focus();
      this.emailInput?.setAttribute('aria-invalid', 'true');
      this.emailInput?.setAttribute('aria-describedby', klaviyoErrorMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  /**
   * @param {string} [message]
   */
  #showError(message) {
    const errorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const klaviyoSuccessMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );

    if (klaviyoSuccessMessage) {
      klaviyoSuccessMessage.hidden = true;
    }

    if (errorMessage) {
      const messageText = errorMessage.querySelector('.email-signup__message-text');
      if (messageText) {
        if (message) {
          messageText.textContent = message;
        } else {
          const defaultText = messageText.getAttribute('data-default-text');
          if (defaultText) {
            messageText.textContent = defaultText;
          }
        }
      }
      errorMessage.hidden = false;
      errorMessage.focus();
      this.emailInput?.setAttribute('aria-describedby', errorMessage.id);
    }

    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
  }

  #filterNativeShopifyMessages() {
    if (this.klaviyoCompanyId) return;

    let last;
    try {
      last = sessionStorage.getItem(NATIVE_SHOPIFY_BLOCK_KEY);
    } catch (e) {
      return;
    }
    if (last == null || last === '' || this.blockId == null) return;

    const isMatch = String(this.blockId) === String(last);

    const shopifyError = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-error-${this.blockId}`)
    );
    const shopifySuccess = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-success-${this.blockId}`)
    );
    if (!isMatch) {
      if (shopifyError) shopifyError.hidden = true;
      if (shopifySuccess) shopifySuccess.hidden = true;
      this.emailInput?.removeAttribute('autofocus');
      this.emailInput?.removeAttribute('aria-invalid');
      this.emailInput?.removeAttribute('aria-describedby');
    }
  }

  #hideMessages() {
    const klaviyoSuccessMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-success-${this.blockId}`)
    );
    const klaviyoErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-klaviyo-error-${this.blockId}`)
    );
    const shopifySuccessMessage = this.form?.querySelector(
      `#Email-signup__message-success-${this.blockId}`
    );
    const shopifyErrorMessage = /** @type {HTMLElement | null} */ (
      this.form?.querySelector(`#Email-signup__message-error-${this.blockId}`)
    );

    if (klaviyoSuccessMessage) klaviyoSuccessMessage.hidden = true;
    if (klaviyoErrorMessage) klaviyoErrorMessage.hidden = true;
    if (shopifySuccessMessage) {
      shopifySuccessMessage.classList.add('email-signup__message--hidden');
    }
    if (shopifyErrorMessage) shopifyErrorMessage.hidden = true;

    this.emailInput?.removeAttribute('aria-invalid');
    this.emailInput?.removeAttribute('aria-describedby');
  }

  /**
   * @param {boolean} loading
   */
  #setButtonLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
  }
}

if (!customElements.get('email-signup-form')) {
  customElements.define('email-signup-form', EmailSignupFormComponent);
}
